import { useMemo, useState } from "react";
import { getApiErrorMessage } from "@/utils/apiError";
import {
  FiCheckSquare,
  FiCreditCard,
  FiGrid,
  FiList,
  FiPieChart,
  FiTag,
  FiTarget,
  FiZap,
} from "react-icons/fi";
import { useAuthGuard } from "@/hooks/useAuthGuard";
import { useAccounts, useFrequentAccounts } from "@/hooks/useAccounts";
import { useBudget, useCreateBudget } from "@/hooks/useBudget";
import { useCategories, useCreateCategory, useFrequentCategories } from "@/hooks/useCategories";
import { useCreateInstallment } from "@/hooks/useInstallments";
import { useCreateSaving } from "@/hooks/useSavings";
import { useCreateTag } from "@/hooks/useTags";
import { useCreateTransactionFromForm, type TransactionFormData } from "@/hooks/useCreateTransactionFromForm";
import { NewAccountModal } from "@/domains/accounts/components/NewAccountModal.tsx";
import { NewBudgetModal } from "@/domains/budget/components/NewBudgetModal.tsx";
import { NewCategoryModal } from "@/domains/categories/components/NewCategoryModal.tsx";
import { InstallmentsDebtFormModal } from "@/domains/installments/components/InstallmentsDebtFormModal.tsx";
import { NewSavingModal } from "@/domains/savings/components/NewSavingModal.tsx";
import { NewSavingPocketModal } from "@/domains/savings/components/NewSavingPocketModal";
import { NewTagModal } from "@/domains/tags/components/NewTagModal.tsx";
import { NewTransactionModal } from "@/domains/transactions/components/NewTransactionModal.tsx";
import { QuickTransactionModal } from "@/domains/transactions/components/QuickTransactionModal.tsx";
import { toLocalYMD } from "@/utils/dateUtils";
import { FloatingActionMenu } from "./FloatingActionMenu.tsx";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { usePreferencesStore } from "@/stores/usePreferencesStore";

type CreateModal =
  | "transaction"
  | "quick-transaction"
  | "account"
  | "budget"
  | "category"
  | "tag"
  | "saving"
  | "saving-pocket"
  | "installment";

const getCurrentMonthRange = () => {
  const now = new Date();

  return {
    billable_date_gte: toLocalYMD(new Date(now.getFullYear(), now.getMonth(), 1)),
    billable_date_lte: toLocalYMD(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

export const GlobalCreateFab = ({
  containerClassName,
}: {
  containerClassName?: string;
}) => {
  const { t } = useTranslation(["common"]);
  const { isAuthenticated } = useAuthGuard();
  const appMode = usePreferencesStore((state) => state.appMode);
  const isAdvancedMode = appMode === "advanced";
  const [activeCreateModal, setActiveCreateModal] = useState<CreateModal | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const isTransactionOpen = activeCreateModal === "transaction" || activeCreateModal === "quick-transaction";
  const { data: categories = [] } = useCategories({ enabled: isTransactionOpen || activeCreateModal === "budget" });
  const { data: accounts = [] } = useAccounts({ enabled: isTransactionOpen || activeCreateModal === "installment" });
  const { data: frequentAccounts = {} } = useFrequentAccounts({ enabled: activeCreateModal === "quick-transaction" });
  const { data: frequentCategories = {} } = useFrequentCategories({ enabled: activeCreateModal === "quick-transaction" });
  const { data: budgetsData } = useBudget({
    ...getCurrentMonthRange(),
    amount: "desc",
  }, { enabled: activeCreateModal === "budget" });
  const createTransactionFromForm = useCreateTransactionFromForm({ enabled: isTransactionOpen });
  const createBudgetMutation = useCreateBudget();
  const createCategoryMutation = useCreateCategory();
  const createTagMutation = useCreateTag();
  const createSavingMutation = useCreateSaving();
  const createInstallmentMutation = useCreateInstallment();

  const assignedCategoryIds = useMemo(
    () =>
      (budgetsData?.budgets_balance ?? [])
        .map((item) => item.category_id)
        .filter((id): id is number => typeof id === "number"),
    [budgetsData],
  );
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );
  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts],
  );

  const closeCreateModal = () => {
    setActiveCreateModal(null);
    setCreateError(null);
  };

  const handleCreateTransaction = async (transactionData: TransactionFormData) => {
    try {
      setCreateError(null);
      await createTransactionFromForm(transactionData);
      closeCreateModal();
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, t("common.errors.createMovement"));
      setCreateError(message);
      throw error instanceof Error ? error : new Error(message);
    }
  };

  const createActions = [
    {
      id: "transaction",
      label: t("common.labels.transaction"),
      description: t("common.createDescriptions.transaction"),
      icon: <FiList className="h-4 w-4" />,
      onSelect: () => setActiveCreateModal("transaction"),
    },
    {
      id: "account",
      label: t("common.labels.account"),
      description: t("common.createDescriptions.account"),
      icon: <FiCreditCard className="h-4 w-4" />,
      onSelect: () => setActiveCreateModal("account"),
    },
    {
      id: "category",
      label: t("common.labels.category"),
      description: t("common.createDescriptions.category"),
      icon: <FiGrid className="h-4 w-4" />,
      onSelect: () => setActiveCreateModal("category"),
    },
    {
      id: "tag",
      label: t("common.labels.tag"),
      description: t("common.createDescriptions.tag"),
      icon: <FiTag className="h-4 w-4" />,
      onSelect: () => setActiveCreateModal("tag"),
    },
    ...(isAdvancedMode && FEATURE_FLAGS.savingPockets
      ? [{
          id: "saving-pocket",
          label: t("common.createLabels.savingPockets"),
          description: t("common.createDescriptions.savingPockets"),
          icon: <FiTarget className="h-4 w-4" />,
          onSelect: () => setActiveCreateModal("saving-pocket"),
        }]
      : []),
    ...(isAdvancedMode
      ? [
          {
            id: "budget",
            label: t("common.labels.budget"),
            description: t("common.createDescriptions.budget"),
            icon: <FiPieChart className="h-4 w-4" />,
            onSelect: () => setActiveCreateModal("budget"),
          },
          {
            id: "installment",
            label: t("common.labels.installment"),
            description: t("common.createDescriptions.installment"),
            icon: <FiCheckSquare className="h-4 w-4" />,
            onSelect: () => setActiveCreateModal("installment"),
          },
          {
            id: "saving",
            label: t("common.createLabels.savingsGoal"),
            description: t("common.createDescriptions.saving"),
            icon: <FiTarget className="h-4 w-4" />,
            onSelect: () => setActiveCreateModal("saving"),
          },
        ]
      : []),
  ];

  if (!isAuthenticated) return null;

  return (
    <>
      {createError ? (
        <div className="app-toast bottom-24 rounded-xl border border-[color:color-mix(in_srgb,var(--app-danger)_26%,var(--app-border)_74%)] bg-[var(--app-danger-soft)] px-4 py-4 text-[var(--app-danger)] sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="font-semibold">{t("common.errors.couldNotCreateItem")}</h4>
              <p className="text-sm opacity-90">{createError}</p>
            </div>
            <button
              type="button"
              onClick={() => setCreateError(null)}
              className="text-sm font-medium text-[color:color-mix(in_srgb,var(--app-danger)_90%,transparent)] hover:text-[var(--app-text)]"
            >
              {t("common.actions.close")}
            </button>
          </div>
        </div>
      ) : null}

      <FloatingActionMenu
        menuId="create"
        ariaLabel={t("common.navigation.openCreateMenu")}
        items={createActions}
        containerClassName={containerClassName}
        containerStyle={{
          right: "var(--app-offset-x)",
          bottom: "calc(env(safe-area-inset-bottom) + var(--app-space-4))",
        }}
      />

      <button
        type="button"
        onClick={() => setActiveCreateModal("quick-transaction")}
        className="app-focus-ring app-fab fixed right-[var(--app-offset-x)] bottom-[calc(env(safe-area-inset-bottom)+var(--app-space-4)+var(--app-fab-size)+var(--app-space-3))] z-50 inline-flex items-center justify-center rounded-full border-[3px] border-[var(--ds-ground)] bg-[var(--ds-card-sun)] text-[var(--ds-on-card)] shadow-[var(--ds-shadow-3)] transition-colors hover:brightness-95"
        style={{ width: "var(--app-fab-size)", height: "var(--app-fab-size)" }}
        aria-label={t("common.navigation.quickTransaction")}
      >
        <FiZap className="h-[26px] w-[26px]" aria-hidden="true" />
      </button>

      <NewTransactionModal
        isOpen={activeCreateModal === "transaction"}
        onClose={closeCreateModal}
        onSubmit={handleCreateTransaction}
      />

      <QuickTransactionModal
        isOpen={activeCreateModal === "quick-transaction"}
        onClose={closeCreateModal}
        onSubmit={handleCreateTransaction}
        frequentAccounts={frequentAccounts}
        frequentCategories={frequentCategories}
        fallbackAccounts={sortedAccounts.map((account) => account.name)}
        fallbackCategories={sortedCategories.map((category) => category.name)}
      />

      <NewAccountModal
        isOpen={activeCreateModal === "account"}
        onClose={closeCreateModal}
        onSuccess={closeCreateModal}
        account={null}
      />

      {isAdvancedMode ? <NewBudgetModal
        isOpen={activeCreateModal === "budget"}
        onClose={closeCreateModal}
        categories={sortedCategories}
        assignedCategoryIds={assignedCategoryIds}
        initialData={null}
        onSubmit={async (data) => {
          await createBudgetMutation.mutateAsync(data);
        }}
      /> : null}

      <NewCategoryModal
        isOpen={activeCreateModal === "category"}
        onClose={closeCreateModal}
        onSubmit={async (data) => {
          await createCategoryMutation.mutateAsync(data);
          closeCreateModal();
        }}
        category={null}
      />

      <NewTagModal
        isOpen={activeCreateModal === "tag"}
        onClose={closeCreateModal}
        onSubmit={async (data) => {
          await createTagMutation.mutateAsync(data);
          closeCreateModal();
        }}
        tag={null}
      />

      {isAdvancedMode ? <NewSavingModal
        isOpen={activeCreateModal === "saving"}
        onClose={closeCreateModal}
        onSubmit={async (data) => {
          await createSavingMutation.mutateAsync({
            name: data.name,
            description: data.description,
            initial_amount: data.initial_amount,
            target_amount: data.target_amount,
            target_date: data.expected_date,
          });
          closeCreateModal();
        }}
        saving={null}
      /> : null}

      {isAdvancedMode && FEATURE_FLAGS.savingPockets ? (
        <NewSavingPocketModal
          isOpen={activeCreateModal === "saving-pocket"}
          onClose={closeCreateModal}
        />
      ) : null}

      {isAdvancedMode ? <InstallmentsDebtFormModal
        isOpen={activeCreateModal === "installment"}
        onClose={closeCreateModal}
        accounts={sortedAccounts}
        initialData={null}
        onSubmit={(data) => {
          createInstallmentMutation.mutate(data);
          closeCreateModal();
        }}
      /> : null}
    </>
  );
};
